import { createAsyncThunk } from "@reduxjs/toolkit";
import AiBatchApiService, {
  toFriendlyAiBatchError,
} from "../apiServices/AiBatchApiService";
import {
  AiBatch,
  AiBatchFilter,
  AiBatchListQuery,
  AiBatchQuota,
  SubmitAiBatchForm,
} from "../../interfaces/AiBatchObject";

/**
 * Async thunks for AI batch image edits.
 *
 * Every thunk rejects with friendly copy, so the slice's errorMap and a
 * component's `.unwrap()` catch both get a display string. Per-row thunks take
 * the batchId as their argument, which the slice reads off `meta.arg` to key
 * per-row loading state — one card's spinner never disables another's buttons.
 */

type ThunkConfig = { rejectValue: string };

/** First page for a filter. Replaces the list (see the slice for the race guard). */
export const fetchAiBatches = createAsyncThunk<AiBatch[], AiBatchFilter, ThunkConfig>(
  "aiBatch/fetchAiBatches",
  async (filter, { rejectWithValue }) => {
    try {
      return await AiBatchApiService.getBatches({ filter });
    } catch (error) {
      return rejectWithValue(toFriendlyAiBatchError(error));
    }
  }
);

/** The next page, appended. `before` is the last loaded row's `createdAt`. */
export const fetchMoreAiBatches = createAsyncThunk<AiBatch[], AiBatchListQuery, ThunkConfig>(
  "aiBatch/fetchMoreAiBatches",
  async (query, { rejectWithValue }) => {
    try {
      return await AiBatchApiService.getBatches(query);
    } catch (error) {
      return rejectWithValue(toFriendlyAiBatchError(error));
    }
  }
);

/** One batch with its items — loaded when a card is expanded. */
export const fetchAiBatchDetail = createAsyncThunk<AiBatch, string, ThunkConfig>(
  "aiBatch/fetchAiBatchDetail",
  async (batchId, { rejectWithValue }) => {
    try {
      return await AiBatchApiService.getBatch(batchId);
    } catch (error) {
      return rejectWithValue(toFriendlyAiBatchError(error));
    }
  }
);

/**
 * Submits a batch. NOT safe to repeat: a second submit for the same collection
 * either spends quota twice or races into PRODUCT_ALREADY_IN_BATCH — callers
 * must block the button while this is in flight.
 */
export const submitAiBatch = createAsyncThunk<AiBatch, SubmitAiBatchForm, ThunkConfig>(
  "aiBatch/submitAiBatch",
  async (form, { rejectWithValue }) => {
    try {
      return await AiBatchApiService.submitBatch(form);
    } catch (error) {
      return rejectWithValue(toFriendlyAiBatchError(error));
    }
  }
);

export const cancelAiBatch = createAsyncThunk<AiBatch, string, ThunkConfig>(
  "aiBatch/cancelAiBatch",
  async (batchId, { rejectWithValue }) => {
    try {
      return await AiBatchApiService.cancelBatch(batchId);
    } catch (error) {
      return rejectWithValue(toFriendlyAiBatchError(error));
    }
  }
);

export const revertAiBatch = createAsyncThunk<AiBatch, string, ThunkConfig>(
  "aiBatch/revertAiBatch",
  async (batchId, { rejectWithValue }) => {
    try {
      return await AiBatchApiService.revertBatch(batchId);
    } catch (error) {
      return rejectWithValue(toFriendlyAiBatchError(error));
    }
  }
);

export const fetchAiBatchQuota = createAsyncThunk<AiBatchQuota, string, ThunkConfig>(
  "aiBatch/fetchAiBatchQuota",
  async (businessId, { rejectWithValue }) => {
    try {
      return await AiBatchApiService.getBatchQuota(businessId);
    } catch (error) {
      return rejectWithValue(toFriendlyAiBatchError(error));
    }
  }
);
